import {
    allowNextNavigation,
    api,
    getTimezone,
    ListChoiceFilter,
    todayInTz,
    useIsPlatformHost,
    useZodForm,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    Button,
    Checkbox,
    DateField,
    DateTime,
    Field,
    FormSheet,
    Input,
    type ListColumnDef,
    PageHeader,
    StatusBadge,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import type { Flag } from '@/api/types';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';
import { useWrite } from '@/lib/queries';
import { ADMIN_STATUSES } from '@/lib/statuses';

const schema = z.object({
    key: z
        .string()
        .regex(
            /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/,
            'service.feature, lower case',
        ),
    description: z.string().min(1, 'Say what it switches'),
    default_on: z.boolean(),
    owner: z.string().min(1, 'An owner'),
    ticket: z.string().min(1, 'The ticket, BM-…'),
    expires_at: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'A date'),
    tenant_overridable: z.boolean(),
});

/** BM-105: whose flag it is: the platform's (applies to every tenant) or this tenant's own, or its override of a platform flag. */
export function FlagScope({ f }: { f: Flag }) {
    if (f.tenant_id === null) {
        return (
            <span className="inline-flex items-center gap-1">
                <StatusBadge
                    status="platform"
                    domain={ADMIN_STATUSES.flagScope}
                />
                {f.overridden ? (
                    <span className="text-muted-foreground text-xs">
                        overridden here
                    </span>
                ) : f.tenant_overridable ? (
                    <span className="text-muted-foreground text-xs">
                        tenants may override
                    </span>
                ) : null}
            </span>
        );
    }
    return <StatusBadge status="tenant" domain={ADMIN_STATUSES.flagScope} />;
}

export function FlagState({ f }: { f: Flag }) {
    if (f.archived_at)
        return <StatusBadge status="archived" domain={ADMIN_STATUSES.flag} />;
    return (
        <span className="inline-flex items-center gap-1">
            <StatusBadge
                status={f.default_on ? 'on' : 'off'}
                domain={ADMIN_STATUSES.flag}
            />
            {f.rules.length > 0 ? (
                <span className="text-muted-foreground text-xs">
                    +{f.rules.length} rules
                </span>
            ) : null}
        </span>
    );
}

const PATH = '/staff/flags';
const PAGE_KEY = 'flags';

/** The route's prefetch: the person's views, then the rows of the view they land on (BM-46 §2.0 + §2.4). */
export const flagsPrefetch = adminListPrefetch(PAGE_KEY, PATH);

const flagFilters = filterDefs(PATH, {
    q: 'Search',
    kind: 'Kind',
    expiring: 'Expiry',
    archived: 'Archived',
    owner: 'Owner',
});

export function FlagsPage() {
    const navigate = useNavigate();
    // BM-105: the platform host edits the platform's flags (every tenant's); a tenant board its own and its overrides
    const platformHost = useIsPlatformHost();
    const [open, setOpen] = useState(false);
    const [error, setError] = useState<unknown>(null);
    const form = useZodForm(schema, {
        defaultValues: {
            key: '',
            description: '',
            default_on: false,
            owner: '',
            ticket: '',
            expires_at: '',
            tenant_overridable: false,
        },
    });
    const create = useWrite(
        async (values: z.infer<typeof schema>) =>
            (
                await api().post<Flag>('/staff/flags', {
                    ...values,
                    rules: [],
                    variants: null,
                })
            ).data,
        ['/staff/flags'],
    );
    const columns: ListColumnDef<Flag>[] = [
        {
            key: 'key',
            label: 'Flag',
            sortKey: 'key',
            boardCard: true,
            cell: (f) => (
                <Link
                    to={`/flags/${f.key}`}
                    className="font-mono text-xs hover:underline"
                >
                    {f.key}
                </Link>
            ),
            locked: 'start',
            noTruncate: true,
        },
        {
            key: 'kind',
            label: 'Kind',
            sortKey: 'kind',
            groupable: { key: 'kind', value: (f) => f.kind },
            cell: (f) => (
                <StatusBadge status={f.kind} domain={ADMIN_STATUSES.flagKind} />
            ),
        },
        {
            key: 'scope',
            label: 'Scope',
            cell: (f) => <FlagScope f={f} />,
            boardCard: true,
        },
        {
            key: 'state',
            label: 'State',
            sortKey: 'default_on',
            cell: (f) => <FlagState f={f} />,
            boardCard: true,
        },
        {
            key: 'description',
            label: 'What it switches',
            cell: (f) => f.description,
        },
        {
            key: 'owner',
            label: 'Owner',
            sortKey: 'owner',
            cell: (f) => f.owner ?? '—',
        },
        {
            key: 'expires_at',
            aliases: ['expires'],
            label: 'Expires',
            sortKey: 'expires_at',
            // a calendar day (YYYY-MM-DD): shown as that day, never moved by a zone
            cell: (f) => (
                <DateTime value={f.expires_at} format="day" empty="—" />
            ),
            noTruncate: true,
        },
    ];
    const { state, query } = useAdminList<Flag>(PAGE_KEY, PATH, {
        columns,
        filters: flagFilters,
    });
    return (
        <div>
            <PageHeader
                title="Feature flags"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            {platformHost
                                ? "The platform's flags apply to every tenant; one marked open may be overridden by a tenant on its board. Release flags expire and get archived; kill switches stay, and flipping one needs a reason."
                                : "This tenant's own flags and its overrides of the platform flags opened to it; the other platform flags are read-only here."}
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
                primaryAction={
                    <Button onClick={() => setOpen(true)}>
                        New release flag
                    </Button>
                }
            />
            <AdminListPage
                noun="flags"
                state={state}
                query={query}
                rowKey={(f) => f.key}
                rowHref={(f) => `/flags/${f.key}`}
                controls={{
                    q: { kind: 'search' },
                    expiring: (
                        <ListChoiceFilter
                            state={state}
                            name="expiring"
                            label="Expiry"
                            options={[{ value: 'true', label: 'Expiring' }]}
                        />
                    ),
                    archived: (
                        <ListChoiceFilter
                            state={state}
                            name="archived"
                            label="Archived"
                            options={[{ value: 'true', label: 'Archived too' }]}
                        />
                    ),
                    owner: { kind: 'text' },
                }}
            />
            {/* BM-84 (rule E.9, design 0.7.0 flags): the create form in a sheet */}
            <FormSheet
                open={open}
                onOpenChange={(o) => {
                    setOpen(o);
                    if (!o) setError(null);
                }}
                size="sm"
                title="New release flag"
                submitLabel="Create flag"
                busy={create.isPending}
                dirty={form.formState.isDirty}
                error={error}
                data-testid="flag-form"
                onSubmit={form.handleSubmit((values) => {
                    setError(null);
                    create.mutate(values, {
                        onSuccess: (f) => {
                            // saved: the reset and the navigation happen in one handler, so tell the guard to let this one through
                            setOpen(false);
                            form.reset();
                            allowNextNavigation();
                            navigate(`/flags/${f.key}`);
                        },
                        onError: setError,
                    });
                })}
            >
                <Field
                    id="key"
                    label="Key"
                    error={form.formState.errors.key?.message}
                >
                    <Input
                        id="key"
                        placeholder="checkout.new_receipt"
                        {...form.register('key')}
                    />
                </Field>
                <Field
                    id="description"
                    label="What it switches"
                    error={form.formState.errors.description?.message}
                >
                    <Input id="description" {...form.register('description')} />
                </Field>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field
                        id="owner"
                        label="Owner"
                        error={form.formState.errors.owner?.message}
                    >
                        <Input id="owner" {...form.register('owner')} />
                    </Field>
                    <Field
                        id="ticket"
                        label="Ticket"
                        error={form.formState.errors.ticket?.message}
                    >
                        <Input
                            id="ticket"
                            placeholder="BM-42"
                            {...form.register('ticket')}
                        />
                    </Field>
                </div>
                <Field
                    id="expires_at"
                    label="Expires"
                    error={form.formState.errors.expires_at?.message}
                >
                    <DateField
                        id="expires_at"
                        value={form.watch('expires_at') || null}
                        min={todayInTz(getTimezone())}
                        invalid={Boolean(form.formState.errors.expires_at)}
                        onChange={(day) =>
                            form.setValue('expires_at', day ?? '', {
                                shouldValidate: form.formState.isSubmitted,
                            })
                        }
                    />
                </Field>
                <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                        checked={form.watch('default_on')}
                        onCheckedChange={(v) =>
                            form.setValue('default_on', v === true)
                        }
                    />
                    On by default
                </label>
                {platformHost ? (
                    <label className="flex items-center gap-2 text-sm">
                        <Checkbox
                            checked={form.watch('tenant_overridable')}
                            onCheckedChange={(v) =>
                                form.setValue('tenant_overridable', v === true)
                            }
                        />
                        Tenants may override it on their boards
                    </label>
                ) : null}
            </FormSheet>
        </div>
    );
}
